"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
/* eslint-disable no-param-reassign */
const three_1 = require("three");
// eslint-disable-next-line import/no-cycle
const SlotCoverSnappingBase_1 = __importDefault(require("./SlotCoverSnappingBase"));
const utils_3d_1 = require("../../utils/utils.3d");
const consts_1 = require("../../consts");
const PanelInSlotsUtils_1 = require("../PanelInSlots/PanelInSlotsUtils");
const SlotCoverUtils_1 = require("./SlotCoverUtils");
const tempVector = new three_1.Vector3();
class GasketInSlotSnapping extends SlotCoverSnappingBase_1.default {
    constructor(viewer, coverComponent) {
        super(viewer, coverComponent);
        this.isEA = this.cover.partDetails.unitOfMeasurementId === 1;
    }
    getMaxComponentLength(toCoverLength) {
        return this.isEA ? this.cover.partDetails.maxStockLength : toCoverLength;
    }
    isOneSideGasket() {
        const gasketBox = (0, utils_3d_1.getAABBox)(this.cover).expandByScalar(consts_1.consts.TWO_FLOAT_EPSILON);
        const { points } = this.cover.getRectangles()[0];
        return !points.every((point) => gasketBox.containsPoint(point));
    }
    snapToInternalSideOfSlot(slot, panel, extrusion, coverEndPoints = []) {
        const resultCovers = [];
        if (!(0, utils_3d_1.isExtrusion)(extrusion) || !(0, utils_3d_1.isPanel)(panel)) {
            throw new Error(`Invalid argument type for panel or extrusion.`);
        }
        if (!this.cover.getRectangles()) {
            console.log(`The component '${this.cover.partDetails.itemNumber}' is missing required metadata.`);
            return resultCovers;
        }
        const gasket = this.cover;
        this.coverPosOnSlot = slot.surfacePoint;
        this.slotDirection = new three_1.Vector3().subVectors(coverEndPoints[1], coverEndPoints[0]).normalize();
        const startPoint = (0, SlotCoverUtils_1.getCoverPoint)(coverEndPoints[0], this.slotDirection.clone().negate(), extrusion);
        const endPoint = (0, SlotCoverUtils_1.getCoverPoint)(coverEndPoints[1], this.slotDirection, extrusion);
        this.panelNormal = (0, PanelInSlotsUtils_1.getPanelNormal)(slot);
        const isOneSideGasket = this.isOneSideGasket();
        let toCoverLength = tempVector.subVectors(startPoint, endPoint).length();
        this.orientCoverOnSlot(slot);
        gasket.rotateOnAxis(consts_1.consts.DP_UP_DIR, Math.PI);
        if (isOneSideGasket) {
            const gasketNormal = (0, SlotCoverUtils_1.getGasketNormal)(gasket);
            if (gasketNormal && !(0, utils_3d_1.areVectorsEqual)(gasketNormal, this.panelNormal)) {
                gasket.rotate(new three_1.Quaternion().setFromAxisAngle(this.slotDirection, Math.PI));
            }
            if (!(0, utils_3d_1.areVectorsEqual)(gasket.directionNormal, this.slotDirection)) {
                gasket.rotate(new three_1.Quaternion().setFromAxisAngle(this.panelNormal, Math.PI));
            }
        }
        const maxComponentLength = this.getMaxComponentLength(toCoverLength);
        const gasketCount = Math.ceil(toCoverLength / maxComponentLength);
        for (let i = 0; i < gasketCount; i += 1) {
            const currLength = toCoverLength <= maxComponentLength ? toCoverLength : maxComponentLength;
            const isFirstCover = i === 0;
            const currentGasket = isFirstCover ? gasket : gasket.clone();
            const startPos = isFirstCover
                ? startPoint.clone()
                : startPoint.clone().add(tempVector.copy(this.slotDirection).multiplyScalar(maxComponentLength * i));
            if (!(0, utils_3d_1.areVectorsEqual)(currentGasket.directionNormal, this.slotDirection)) {
                startPos.add(tempVector.copy(this.slotDirection).multiplyScalar(currLength));
            }
            currentGasket.setScaleZ(currLength);
            currentGasket.setPosition(startPos);
            const { points } = this.cover.getRectangles()[0];
            if (isOneSideGasket) {
                const negateDirection = this.panelNormal.clone().negate();
                const gasketPoint = (0, SlotCoverUtils_1.getTopPoint)(points, negateDirection);
                const slotPoint = (0, SlotCoverUtils_1.getTopPoint)(slot.points, negateDirection);
                const horizontalShift = tempVector
                    .subVectors(slot.surfacePoint, gasketPoint)
                    .projectOnVector(slot.normal)
                    .clone();
                const verticalShift = tempVector.subVectors(slotPoint, gasketPoint).projectOnVector(this.panelNormal);
                const gasketOffset = horizontalShift.add(verticalShift);
                currentGasket.translate(gasketOffset);
            }
            else {
                const slotCenter = new three_1.Box3().setFromPoints(slot.points).getCenter(new three_1.Vector3());
                const gasketCenter = new three_1.Box3().setFromPoints(points).getCenter(new three_1.Vector3());
                const horizontalShift = tempVector.subVectors(slotCenter, gasketCenter).projectOnVector(slot.normal).clone();
                const verticalShift = tempVector.subVectors(slotCenter, gasketCenter).projectOnVector(this.panelNormal);
                const gasketOffset = horizontalShift.add(verticalShift);
                currentGasket.translate(gasketOffset);
            }
            toCoverLength -= currLength;
            currentGasket.snapped = true;
            currentGasket.isRotatedForSnapping = true;
            resultCovers.push(currentGasket);
        }
        return resultCovers;
    }
    fixPanelPosition(panel, undo = false) {
        var _a;
        const currentGasket = this.cover;
        const slot = (_a = currentGasket.getSlots()) === null || _a === void 0 ? void 0 : _a.at(0);
        const isOneSideGasket = this.isOneSideGasket();
        if (!slot || !isOneSideGasket) {
            return;
        }
        if (undo) {
            if (panel.basePosition) {
                panel.setPosition(panel.basePosition);
                panel.basePosition = null;
            }
            return;
        }
        if (!panel.basePosition) {
            const panelPosition = panel.position.clone();
            this.panelNormal = (0, PanelInSlotsUtils_1.getPanelNormal)(slot);
            this.setPanelShift(panel, slot, currentGasket);
            panel.basePosition = panelPosition.clone();
        }
    }
    setPanelShift(panel, slot, gasket) {
        const gasketBox = (0, utils_3d_1.getAABBox)(gasket);
        const gasketBoxCorners = (0, utils_3d_1.getBox3Corners)(gasketBox);
        const gasketBoxPlanes = (0, utils_3d_1.getBox3Planes)(gasketBox);
        const gasketBottomPlane = gasketBoxPlanes.find((plane) => (0, utils_3d_1.areVectorsEqual)(plane.normal, this.panelNormal));
        if (gasketBottomPlane) {
            const gasketBottomPoint = gasketBoxCorners.find((point) => (0, utils_3d_1.roundTo3)(gasketBottomPlane.distanceToPoint(point)) === 0);
            if (gasketBottomPoint) {
                const direction = this.panelNormal;
                const coordinateIndex = (0, utils_3d_1.getWorldAxisIndex)(direction);
                const panelHeight = panel.getLength();
                const slotPoint = (0, SlotCoverUtils_1.getTopPoint)(slot.points, direction);
                const nearestPoint = (0, utils_3d_1.getNearestPoint)(gasketBottomPoint.clone().projectOnVector(direction), [
                    gasketBottomPoint.clone().projectOnVector(direction).add(direction.clone().multiplyScalar(panelHeight)),
                    slotPoint.clone().projectOnVector(direction),
                ]);
                if ((0, utils_3d_1.areEqual)(nearestPoint.getComponent(coordinateIndex), slotPoint.getComponent(coordinateIndex))) {
                    const panelPositionValue = slotPoint
                        .clone()
                        .sub(direction.multiplyScalar(panelHeight))
                        .getComponent(coordinateIndex);
                    panel.setPositionComponent(coordinateIndex, panelPositionValue);
                }
                else {
                    const panelPositionValue = gasketBottomPoint
                        .clone()
                        .add(direction.clone().multiplyScalar(panelHeight))
                        .getComponent(coordinateIndex);
                    panel.setPositionComponent(coordinateIndex, panelPositionValue);
                }
            }
        }
    }
}
exports.default = GasketInSlotSnapping;
